import { Badge, useCurrentTimestamp, useDateFnsFormattersWithLocale } from "@tellescope/ui";
import { AvailabilityTimeRange, formatRangeDuration } from "../../utils";
import { useCallback } from "react";
import { toZonedTime } from "date-fns-tz";
import { isSameDay } from "date-fns/isSameDay";
import { useTranslation } from "react-i18next";
import { isSameWeek } from "date-fns/isSameWeek";
import { Link } from "react-router-dom";
import { startOfMinute } from "date-fns/startOfMinute";

export interface AvailabilityRangeButtonProps {
  range: AvailabilityTimeRange;
  weekStart?: "sunday" | "monday";
  timezone?: string;
  onClick: (() => void) | { to: string; replace?: boolean };
  couldBeNow?: boolean;
}

export const AvailabilityRangeButton = ({
  range,
  weekStart,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  onClick,
  couldBeNow = true,
}: AvailabilityRangeButtonProps) => {
  const { t } = useTranslation("calendar");
  const now = useCurrentTimestamp({ pollingRateInMs: couldBeNow ? 1000 * 60 : 0, snapTo: "minute" });
  const weekStartsOn = weekStart === "monday" ? 1 : 0;
  const zonedNow = toZonedTime(now, timezone);
  const { formatInTimeZone } = useDateFnsFormattersWithLocale();
  const Component = typeof onClick === "function" ? "button" : Link;

  const formatDayLabel = useCallback(
    (date: Date) => {
      const zonedDate = toZonedTime(date, timezone);
      if (isSameDay(zonedDate, zonedNow)) {
        return t("Today");
      }
      if (isSameWeek(zonedDate, zonedNow, { weekStartsOn })) {
        return formatInTimeZone(date, timezone, "EEEE");
      }
      return formatInTimeZone(date, timezone, "MMM d");
    },
    [t, formatInTimeZone, timezone, zonedNow, weekStartsOn]
  );

  return (
    <Component
      to={typeof onClick === "object" ? onClick.to : ""}
      replace={typeof onClick === "object" ? onClick.replace : undefined}
      onClick={typeof onClick === "function" ? onClick : undefined}
    >
      <Badge
        variant="outline"
        className="h-7 px-1.5 text-xs rounded-full border-emerald-500/40 bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 hover:text-emerald-600 font-light"
      >
        {`${formatDayLabel(range.start)} · ${
          startOfMinute(range.start).getTime() === startOfMinute(now).getTime()
            ? t("Now")
            : formatInTimeZone(range.start, timezone, "h:mm a zz")
        } · ${formatRangeDuration(range.start, range.end)}`}
      </Badge>
    </Component>
  );
};
